import * as THREE from "three";
import { FloorplannerStore } from "../../../store/floorplannerStore";
import { trackFloorplan3DMetric } from "../metrics/floorplan3DMetrics";

export type FloorplanRenderManifest = {
  version: 1;
  createdAt: string;
  camera?: {
    type: string;
    position: [number, number, number];
    rotation: [number, number, number];
    fov?: number;
    zoom?: number;
  };
  viewport?: { width: number; height: number; dpr: number };
  lighting: FloorplannerStore["lighting"];
  catalogRefs: {
    products: string[];
    surfaces: string[];
    materials: string[];
  };
  /**
   * Counts only. Full wall/symbol trees (especially product + material
   * assignments) can be several MB and 413 the GraphQL upload. The server
   * prompt uses the snapshot image, not this geometry.
   */
  geometry: {
    wallCount: number;
    floorCount: number;
    symbolCount: number;
  };
};

export function createFloorplanRenderManifest(input: {
  store: FloorplannerStore;
  camera?: THREE.Camera | null;
  viewport?: { width: number; height: number; dpr?: number };
}): FloorplanRenderManifest {
  const { store, camera, viewport } = input;
  const symbols = Array.from(store.symbolsMap.values());
  const walls = Array.from(store.wallsMap.values());
  const surfaces = new Set<string>();
  const materials = new Set<string>();
  const products = new Set<string>();

  const collectSurfaceRef = (ref: any) => {
    if (!ref?.id) return;
    if (ref.source === "material") materials.add(ref.id);
    else if (ref.source === "product") products.add(ref.id);
    else surfaces.add(ref.id);
  };

  walls.forEach((wall) => collectSurfaceRef(wall.threeD?.surfaceRef));
  symbols.forEach((symbol) => {
    collectSurfaceRef(symbol.threeD?.surfaceRef);
    if (symbol.threeD?.product?.productId) {
      products.add(symbol.threeD.product.productId);
    }
    Object.values(symbol.threeD?.materialAssignments ?? {}).forEach(
      (assignment: any) => {
        if (assignment?.materialId) materials.add(assignment.materialId);
        collectSurfaceRef(assignment?.surfaceRef);
      },
    );
  });

  const manifest: FloorplanRenderManifest = {
    version: 1,
    createdAt: new Date().toISOString(),
    camera: camera ? serializeCamera(camera) : undefined,
    viewport: viewport
      ? {
          width: viewport.width,
          height: viewport.height,
          dpr: viewport.dpr ?? window.devicePixelRatio,
        }
      : undefined,
    lighting: { ...store.lighting },
    catalogRefs: {
      products: Array.from(products),
      surfaces: Array.from(surfaces),
      materials: Array.from(materials),
    },
    geometry: {
      wallCount: walls.length,
      floorCount:
        symbols.filter((symbol) => symbol.type === "areaFill").length +
        store.roomsMap.size,
      symbolCount: symbols.length,
    },
  };

  trackFloorplan3DMetric("ai_render_manifest_created", {
    wallCount: manifest.geometry.wallCount,
    symbolCount: manifest.geometry.symbolCount,
    productCount: manifest.catalogRefs.products.length,
    surfaceCount: manifest.catalogRefs.surfaces.length,
  });

  return manifest;
}

export function captureFloorplanCanvasSnapshot(
  canvas: HTMLCanvasElement,
): Promise<File> {
  return new Promise((resolve, reject) => {
    const filename = `floorplan_3d_snapshot_${Date.now()}.png`;
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Could not capture 3D canvas snapshot"));
          return;
        }
        resolve(new File([blob], filename, { type: "image/png" }));
      }, "image/png");
      return;
    }

    if (typeof canvas.toDataURL === "function") {
      try {
        resolve(dataUrlToFile(canvas.toDataURL("image/png"), filename));
      } catch (error) {
        reject(error);
      }
      return;
    }

    reject(new Error("3D canvas snapshot capture is unavailable"));
  });
}

function dataUrlToFile(dataUrl: string, filename: string): File {
  const [metadata, data] = dataUrl.split(",");
  const mime = metadata.match(/data:(.*?);base64/)?.[1] ?? "image/png";
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], filename, { type: mime });
}

function serializeCamera(camera: THREE.Camera): FloorplanRenderManifest["camera"] {
  const perspective = camera as THREE.PerspectiveCamera;
  return {
    type: camera.type,
    position: [camera.position.x, camera.position.y, camera.position.z],
    rotation: [camera.rotation.x, camera.rotation.y, camera.rotation.z],
    fov: typeof perspective.fov === "number" ? perspective.fov : undefined,
    zoom: "zoom" in camera ? (camera as THREE.PerspectiveCamera).zoom : undefined,
  };
}
